import {
  ChatbotFlowEngine,
  Logger,
  type FlowActionDefinition,
  type FlowAsyncResult,
  type FlowContext,
  type FlowHandlerRegistry,
  type FlowSkipIfRegistry,
  type FlowState,
  type FlowStepDefinition,
  type FlowStepRef,
} from '@footsteps/shared';
import { SupabaseApiService } from '../../services/supabase-api';
import { FindAddress, FetchAddress, DescribeComplaints, ClassifyComplaints, GetFlow, MoreComplaints } from './steps';
import type { AddressByIdResponse } from '../../services/supabase-api/supabase-api.types';
import type { WozCaseComplaint, WozCaseQuestion } from '../woz-case.types';
import { FlowStepId } from './woz-flow.types';
import { getWozLatestValue } from '@/utils/woz';

export class WozFlow {
  private static readonly logger = new Logger('WozFlow');
  private static asyncHandlerRegistry: FlowHandlerRegistry = {};

  private static skipIfRegistry: FlowSkipIfRegistry = {};

  static {
    WozFlow.asyncHandlerRegistry[FlowStepId.FetchAddress] = WozFlow.fetchAddressHandler;
    WozFlow.asyncHandlerRegistry[FlowStepId.ClassifyComplaints] = WozFlow.classifyComplaintsHandler;
    WozFlow.asyncHandlerRegistry[FlowStepId.ClassifyComplaintsRetry] = WozFlow.classifyComplaintsHandler;
    WozFlow.asyncHandlerRegistry[FlowStepId.GetFlow] = WozFlow.getFlowHandler;
    WozFlow.skipIfRegistry[FlowStepId.FindAddress] = FindAddress.findAddressStep.skipIf!;
    WozFlow.skipIfRegistry[FlowStepId.FetchAddress] = FetchAddress.fetchAddressStep.skipIf!;
    WozFlow.skipIfRegistry[FlowStepId.ClassifyComplaints] = ClassifyComplaints.classifyComplaintsStep.skipIf!;
  }

  static readonly initialState: FlowState = ChatbotFlowEngine.initialState;

  /** Return a fresh copy of the initial flow state (safe to mutate). */
  static createInitialState(): FlowState {
    return ChatbotFlowEngine.createInitialState();
  }

  static readonly defaultSteps: FlowStepDefinition[] = [
    FindAddress.findAddressStep,
    { ...FetchAddress.fetchAddressStep, handler: WozFlow.fetchAddressHandler },
    DescribeComplaints.describeComplaintsStep,
    { ...ClassifyComplaints.classifyComplaintsStep, handler: WozFlow.classifyComplaintsHandler },
    { ...GetFlow.getFlowStep, handler: WozFlow.getFlowHandler },
  ];

  /** Return a fresh copy of the default flow steps (safe to mutate). */
  static createDefaultSteps(): FlowStepDefinition[] {
    return [...WozFlow.defaultSteps];
  }

  static createDescribeComplaintsRepeatStep = DescribeComplaints.createDescribeComplaintsRepeatStep;

  static createClassifyComplaintsRepeatStep(): FlowStepDefinition {
    return ClassifyComplaints.createClassifyComplaintsRepeatStep(WozFlow.classifyComplaintsHandler);
  }

  static createGetFlowRepeatStep(): FlowStepDefinition {
    return GetFlow.createGetFlowRepeatStep(WozFlow.getFlowHandler);
  }

  static createFlowCompleteStep(): FlowStepDefinition {
    return { ...MoreComplaints.flowCompleteStep };
  }

  /**
   * Registry of static step definitions indexed by their FlowStepId.
   * Used by `rehydrateFromRefs()` to look up full definitions.
   */
  private static staticStepRegistry = new Map<FlowStepId, FlowStepDefinition>([
    [FlowStepId.FindAddress, FindAddress.findAddressStep],
    [FlowStepId.FetchAddress, { ...FetchAddress.fetchAddressStep, handler: WozFlow.fetchAddressHandler }],
    [FlowStepId.AddressUnavailable, FetchAddress.addressUnavailableStep],
    [FlowStepId.AddressFetchError, FetchAddress.addressFetchErrorStep],
    [FlowStepId.DescribeComplaints, DescribeComplaints.describeComplaintsStep],
    [
      FlowStepId.ClassifyComplaints,
      { ...ClassifyComplaints.classifyComplaintsStep, handler: WozFlow.classifyComplaintsHandler },
    ],
    [
      FlowStepId.ClassifyComplaintsRetry,
      { id: FlowStepId.ClassifyComplaintsRetry, type: 'async', handler: WozFlow.classifyComplaintsHandler },
    ],
    [FlowStepId.ClassificationError, ClassifyComplaints.classificationErrorStep],
    [FlowStepId.ClassificationFailed, ClassifyComplaints.classificationFailedStep],
    [FlowStepId.ClassificationNoRelevant, ClassifyComplaints.classificationNoRelevantStep],
    [FlowStepId.NoRelevantRetryOrFinish, ClassifyComplaints.noRelevantRetryOrFinishStep],
    [FlowStepId.DescribeComplaintsRetry, ClassifyComplaints.describeComplaintsRetryStep],
    [FlowStepId.GetFlow, { ...GetFlow.getFlowStep, handler: WozFlow.getFlowHandler }],
    [FlowStepId.GetFlowError, GetFlow.getFlowErrorStep],
    [FlowStepId.AskMoreComplaints, MoreComplaints.askMoreComplaintsStep],
    [FlowStepId.FlowComplete, MoreComplaints.flowCompleteStep],
  ]);

  /**
   * Reconstruct full `FlowStepDefinition[]` from a list of `FlowStepRef`s.
   * - Static steps are looked up from the registry (by clean enum ID).
   * - Dynamic question/intro steps are rebuilt using the `meta` data.
   * - Dynamic repeat steps are delegated to their step-file helpers.
   *
   * @param stepRefs        Ordered list of `FlowStepRef`s persisted in `WozCaseFlowState`.
   * @param complaints  The enriched complaints from the case (with questions).
   */
  static rehydrateFromRefs(stepRefs: FlowStepRef[], complaints: WozCaseComplaint[]): FlowStepDefinition[] {
    return stepRefs.map((stepRef) => {
      switch (stepRef.id as FlowStepId) {
        // ── Dynamic question steps ──────────────────────────────────
        case FlowStepId.GetFlowQuestion:
          return WozFlow.rebuildQuestionStep(stepRef, complaints);

        // ── Dynamic intro steps ─────────────────────────────────────
        case FlowStepId.GetFlowIntro: {
          const idx = (stepRef.meta?.complaintIndex as number) ?? 0;
          const complaint = complaints[idx];
          const text = complaint?.correctedText ?? complaint?.text ?? '';
          const step = GetFlow.complaintIntroStep(idx, complaints.length, text);
          step.sequence = stepRef.sequence;
          return step;
        }

        // ── Dynamic text-dependent steps ────────────────────────────
        case FlowStepId.ConfirmSingleComplaint: {
          const text = complaints[0]?.correctedText ?? complaints[0]?.text ?? '';
          const step = ClassifyComplaints.confirmSingleComplaintStep(text);
          step.sequence = stepRef.sequence;
          return step;
        }

        case FlowStepId.ChooseComplaintMode: {
          const list = complaints
            .map((complaint, i) => `${i + 1}. ${complaint.correctedText ?? complaint.text}`)
            .join('\n');
          const step = ClassifyComplaints.chooseComplaintModeStep(list);
          step.sequence = stepRef.sequence;
          return step;
        }

        case FlowStepId.AddressInfo: {
          const step = FetchAddress.addressInfoStep('Gegevens van je woning opgehaald.');
          step.sequence = stepRef.sequence;
          return step;
        }

        // ── Steps that need special rehydration (repeat steps with handlers) ──
        case FlowStepId.DescribeComplaints: {
          const staticStep = WozFlow.staticStepRegistry.get(FlowStepId.DescribeComplaints)!;
          if (staticStep.sequence === stepRef.sequence) {
            return { ...staticStep };
          }
          return DescribeComplaints.rehydrateDescribeComplaintsStep(stepRef);
        }

        case FlowStepId.ClassifyComplaints: {
          const staticStep = WozFlow.staticStepRegistry.get(FlowStepId.ClassifyComplaints)!;
          if (staticStep.sequence === stepRef.sequence) {
            return { ...staticStep };
          }
          return ClassifyComplaints.rehydrateClassifyComplaintsStep(stepRef, WozFlow.classifyComplaintsHandler);
        }

        case FlowStepId.GetFlow: {
          const staticStep = WozFlow.staticStepRegistry.get(FlowStepId.GetFlow)!;
          if (staticStep.sequence === stepRef.sequence) {
            return { ...staticStep };
          }
          return GetFlow.rehydrateGetFlowStep(stepRef, WozFlow.getFlowHandler);
        }

        // ── All other static steps ──────────────────────────────────
        default: {
          const staticStep = WozFlow.staticStepRegistry.get(stepRef.id as FlowStepId);
          if (staticStep) {
            return { ...staticStep, sequence: stepRef.sequence, meta: stepRef.meta };
          }

          // Fallback — create a noop message step so the engine doesn't crash
          WozFlow.logger.warn(`rehydrateFromRefs: unknown step ID "${stepRef.id}"`);
          return { id: stepRef.id, sequence: stepRef.sequence, type: 'message' as const, text: '' };
        }
      }
    });
  }

  /**
   * Rebuild a question step from the complaint's persisted questions.
   * Uses `meta.complaintIndex` and `meta.questionIndex` instead of parsing IDs.
   */
  private static rebuildQuestionStep(ref: FlowStepRef, complaints: WozCaseComplaint[]): FlowStepDefinition {
    const complaintIdx = (ref.meta?.complaintIndex as number) ?? 0;
    const questionIdx = (ref.meta?.questionIndex as number) ?? 0;
    const complaint = complaints[complaintIdx];
    const question: WozCaseQuestion | undefined = complaint?.questions?.[questionIdx];

    if (!question) {
      WozFlow.logger.warn(
        `rebuildQuestionStep: no question found for complaint=${complaintIdx} question=${questionIdx}`
      );
      return {
        id: FlowStepId.GetFlowQuestion,
        sequence: ref.sequence,
        meta: ref.meta,
        type: 'message' as const,
        text: '',
      };
    }

    if (question.type === 'action' && question.action) {
      const step = GetFlow.questionActionTypeStep(complaintIdx, questionIdx, question.question, question.action);
      step.sequence = ref.sequence;
      return step;
    }

    if (question.type === 'open') {
      const step = GetFlow.questionOpenStep(complaintIdx, questionIdx, question.question);
      step.sequence = ref.sequence;
      return step;
    }

    const sorted = [...question.options].sort((a, b) => a.order - b.order);

    if (question.type === 'multiple-select') {
      const options = sorted.map((option) => ({
        id: option.id,
        label: option.caption,
      }));
      const step = GetFlow.questionMultipleSelectStep(complaintIdx, questionIdx, question.question, options);
      step.sequence = ref.sequence;
      return step;
    }

    const actions: FlowActionDefinition[] = sorted.map((option) => ({
      id: option.id,
      label: option.caption,
      event: 'trigger',
      payload: FlowStepId.GetFlowQuestion,
    }));
    const step = GetFlow.questionActionStep(complaintIdx, questionIdx, question.question, actions);
    step.sequence = ref.sequence;
    return step;
  }

  private static async fetchAddressHandler(context: FlowContext): Promise<FlowAsyncResult> {
    const currentAddressData = context.addressData as AddressByIdResponse | undefined;

    if (!currentAddressData?.addressPointId) {
      return {
        context: { __kadasterFetched: true, addressData: { ...currentAddressData, kadaster: null } },
        insertSteps: [FetchAddress.addressUnavailableStep],
      };
    }

    const api = SupabaseApiService.getInstance();
    let addressData: AddressByIdResponse;
    try {
      addressData = await api.fetchAddressById(currentAddressData.addressPointId);
    } catch (error) {
      WozFlow.logger.error('Address fetch failed', error);
      return {
        context: { __kadasterFetched: true, addressData: { ...currentAddressData, kadaster: null } },
        insertSteps: [FetchAddress.addressFetchErrorStep],
      };
    }

    const kadaster = addressData.kadaster;
    const lines: string[] = ['**WOZ gegevens van je woning:**'];
    if (kadaster?.wozObjectNumber) lines.push(`🏠 Identificatienummer: ${kadaster.wozObjectNumber}`);
    if (kadaster?.wozValues?.length) {
      const latestValue = getWozLatestValue(kadaster.wozValues);
      if (latestValue) {
        lines.push(`💰 Waarde op peildatum ${latestValue.referenceDate}: ${latestValue.assessedValue}`);
      }
    }
    if (kadaster?.gboM2) lines.push(`📐 Gebruiksoppervlakte: ${kadaster.gboM2} m²`);
    if (kadaster?.plotArea) lines.push(`🏗️ Perceeloppervlakte: ${kadaster.plotArea} m²`);
    if (lines.length === 1) {
      lines.push('Gegevens van je woning opgehaald, maar helaas geen WOZ details beschikbaar.');
    }
    return {
      context: { __kadasterFetched: true, addressData },
      insertSteps: [FetchAddress.addressInfoStep(lines.join('\n'))],
    };
  }

  /**
   * Processes one complaint at a time.
   * Fetches questions for the current complaint, inserts question steps,
   * and queues the next complaint's get-flow step if more remain.
   */
  private static async getFlowHandler(context: FlowContext): Promise<FlowAsyncResult> {
    const resolvedComplaints = context.resolvedComplaints as WozCaseComplaint[] | undefined;
    const relevantComplaints = context.relevantComplaints as WozCaseComplaint[] | undefined;
    const complaints = resolvedComplaints ?? relevantComplaints ?? [];
    const currentIndex = (context.currentComplaintIndex as number | undefined) ?? 0;

    if (complaints.length === 0 || currentIndex >= complaints.length) {
      return {
        context: { currentComplaintIndex: currentIndex },
        insertSteps: [GetFlow.getFlowErrorStep],
      };
    }

    const complaint = complaints[currentIndex];
    const insertSteps: FlowStepDefinition[] = [];

    const skipFlow = complaint.categoryCode === 'grief-other';
    let questions: NonNullable<Awaited<ReturnType<typeof SupabaseApiService.prototype.getFlow>>>['questions'] = [];
    let flow: Awaited<ReturnType<typeof SupabaseApiService.prototype.getFlow>> = null;

    if (!skipFlow) {
      const api = SupabaseApiService.getInstance();
      try {
        flow = await api.getFlow({
          categoryCode: complaint.categoryCode,
          text: complaint.text,
          correctedText: complaint.correctedText,
          hash: complaint.hash,
        });
      } catch (error) {
        WozFlow.logger.error('Get flow failed', error);
        return {
          context: { currentComplaintIndex: currentIndex },
          insertSteps: [GetFlow.getFlowErrorStep],
        };
      }
      questions = flow?.questions ?? [];
    }

    // Intro message for the current complaint
    insertSteps.push(
      GetFlow.complaintIntroStep(currentIndex, complaints.length, complaint.correctedText ?? complaint.text, questions.length > 0)
    );

    const offset = (context.__complaintOffset as number | undefined) ?? 0;
    const absoluteIndex = offset + currentIndex;

    questions.forEach((question, qIndex) => {
      if (question.type === 'action' && question.action) {
        insertSteps.push(GetFlow.questionActionTypeStep(absoluteIndex, qIndex, question.question, question.action));
      } else if (question.type === 'open') {
        insertSteps.push(GetFlow.questionOpenStep(absoluteIndex, qIndex, question.question));
      } else if (question.type === 'multiple-select') {
        const sorted = [...question.options].sort((a, b) => a.order - b.order);
        const options = sorted.map((option) => ({
          id: option.id,
          label: option.caption,
        }));
        insertSteps.push(GetFlow.questionMultipleSelectStep(absoluteIndex, qIndex, question.question, options));
      } else {
        const sorted = [...question.options].sort((a, b) => a.order - b.order);
        const actions: FlowActionDefinition[] = sorted.map((option) => ({
          id: option.id,
          label: option.caption,
          event: 'trigger',
          payload: FlowStepId.GetFlowQuestion,
        }));
        insertSteps.push(GetFlow.questionActionStep(absoluteIndex, qIndex, question.question, actions));
      }
    });

    // If more complaints remain, queue the next get-flow step
    const nextIndex = currentIndex + 1;
    if (nextIndex < complaints.length) {
      insertSteps.push({
        ...GetFlow.getFlowStep,
        handler: WozFlow.getFlowHandler,
        meta: { complaintIndex: nextIndex },
      });
    } else {
      insertSteps.push(MoreComplaints.askMoreComplaintsStep);
    }

    // Store questions on the complaint for persistence.
    const caseQuestions: WozCaseQuestion[] = questions.map((question) => ({
      id: question.id,
      question: question.question,
      type: question.type,
      action: question.action,
      order: question.order,
      options: question.options.map((option) => ({
        id: option.id,
        caption: option.caption,
        order: option.order,
      })),
      answer: null,
    }));

    return {
      context: {
        currentComplaintIndex: nextIndex,
        [`flow_${currentIndex}`]: flow,
        __updateComplaintQuestions: { complaintIndex: absoluteIndex, questions: caseQuestions },
      },
      insertSteps,
    };
  }

  private static async classifyComplaintsHandler(context: FlowContext): Promise<FlowAsyncResult> {
    const text = context.complaints as string;
    const api = SupabaseApiService.getInstance();

    let classification: Awaited<ReturnType<typeof api.classifyComplaints>>;
    try {
      classification = await api.classifyComplaints(text);
    } catch (error) {
      WozFlow.logger.error('Classification API failed', error);
      return {
        context: { classification: null },
        insertSteps: [
          ClassifyComplaints.classificationErrorStep,
          ClassifyComplaints.describeComplaintsRetryStep,
          { id: FlowStepId.ClassifyComplaintsRetry, type: 'async', handler: WozFlow.classifyComplaintsHandler },
        ],
      };
    }

    if (!classification || classification.complaints.length === 0) {
      return {
        context: { classification: null },
        insertSteps: [
          ClassifyComplaints.classificationFailedStep,
          ClassifyComplaints.describeComplaintsRetryStep,
          { id: FlowStepId.ClassifyComplaintsRetry, type: 'async', handler: WozFlow.classifyComplaintsHandler },
        ],
      };
    }

    const relevant = classification.complaints.filter((complaint) => complaint.isRelevant);

    if (relevant.length === 0) {
      return {
        context: { classification: null },
        insertSteps: [ClassifyComplaints.classificationNoRelevantStep, ClassifyComplaints.noRelevantRetryOrFinishStep],
      };
    }

    if (relevant.length === 1) {
      const complaint = relevant[0];
      return {
        context: { classification, relevantComplaints: relevant },
        insertSteps: [ClassifyComplaints.confirmSingleComplaintStep(complaint.correctedText ?? complaint.text)],
      };
    }

    const complaintList = relevant
      .map((complaint, index) => `${index + 1}. ${complaint.correctedText ?? complaint.text}`)
      .join('\n');

    return {
      context: { classification, relevantComplaints: relevant },
      insertSteps: [ClassifyComplaints.chooseComplaintModeStep(complaintList)],
    };
  }
}
